<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="600px"
    :disabled="store.state.isWorkflowDisabled"
  >
    <v-form ref="entityForm">
      <v-card>
        <v-row class="fluid justify-space-between align-top ma-2">
          <v-card-title class="d-flex align-center">
            <span class="text-h5"><span class="rounded-lg px-1">Create Entity</span></span>
            <help-dialog-workflow-create-entity />
          </v-card-title>
          <v-btn variant="text" icon color="grey" @click="toggleDialog(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
        <v-card-text>
          <v-container grid-list-md>
            <v-radio-group v-model="type">
              <v-radio value="ai-workflow" :disabled="!workflow.eventsJson || workflow.eventsJson.length === 0">
                <template v-slot:label>
                  <div class="d-flex align-center">Generate all missing entities based on the workflow&nbsp;<v-icon>mdi-creation</v-icon></div>
                </template>
              </v-radio>
              <div class="fit-content">
                <v-radio value="ai-description">
                  <template v-slot:label>
                    <div class="d-flex align-center">Generate one or many entities based on a provided description&nbsp;<v-icon>mdi-creation</v-icon></div>
                  </template>
                </v-radio>
              </div>
              <div v-if="type === 'ai-description'" class="pl-10"  >
                <v-textarea
                  :rules="[rules.required]"
                  auto-grow
                  rows="3"
                  class="no-border"
                  variant="outlined"
                  v-model="aiDescription"
                  placeholder="Description"
                />
              </div>
              <v-radio label="Empty table" value="empty" data-test="entity-empty-example" />
            </v-radio-group>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" size="large" @click="toggleDialog(false)">Cancel</v-btn>
          <v-btn variant="flat" size="large" data-test="add-entity" @click="addEntity()">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import usePrompts from '@/composables/usePrompts'
import safeStructuredClone from '@/utils/clone'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import HelpDialogWorkflowCreateEntity from '@/components/HelpDialogWorkflowCreateEntity.vue'

const { modelValue, entityName } = defineProps({ modelValue: Boolean, entityName: String })
const emit = defineEmits(['update:modelValue'])

const store = useStore()
const { proxy } = getCurrentInstance()
const { getEntitiesFromWorkflow, getEntitiesFromDescription } = usePrompts()

const type = ref('ai-description')
const aiDescription = ref('')
const rules = ref({ required: (value) => !!value || 'Required' })
const entityForm = ref(null)
const workflow = computed(() => store.state.workflow.workflow)

const toggleDialog = (value) => {
  emit('update:modelValue', value)
  if (!value) {
    aiDescription.value = ''
    type.value = 'ai-description'
  }
}

const addEntity = async () => {
  let dataModelsOnWorkflow
  let suggestedEntitiesExist = false

  const validationResult = await entityForm.value.validate()
  if (!validationResult?.valid) {
    return
  }

  if (type.value === 'ai-workflow' || type.value === 'ai-description') {
    if (!workflow.value.aiDescription) {
      store.commit('snackbar/showMessage', {
        content: 'Please enter a description in settings: Get help from AI to build your workflow',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    proxy.$loadingState.start()

    const res = type.value === 'ai-workflow'
      ? await getEntitiesFromWorkflow()
      : await getEntitiesFromDescription(aiDescription.value)

    if (!res?.entities) {
      store.commit('snackbar/showMessage', {
        content: type.value === 'ai-workflow'
          ? 'The AI was unable to generate entities.'
          : 'The AI was unable to generate entities. Please try adjusting the description.',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })

      proxy.$loadingState.stop()
      return
    }

    dataModelsOnWorkflow = safeStructuredClone(workflow.value.dataModels || [])
    let existingEntitiesCount = 0

    for (const entityFromLLM of res.entities) {
      const index = (workflow.value.dataModels || []).findIndex(model => model.name === entityFromLLM.name)

      if (index >= 0) {
        existingEntitiesCount++
        continue // Skip if already exists
      }

      const dimensionTable = workflowHelpers.methods.convertLLMResponseToEntity(entityFromLLM)
      dataModelsOnWorkflow = [...dataModelsOnWorkflow, dimensionTable]
    }

    if (existingEntitiesCount === res.entities.length) {
      suggestedEntitiesExist = true
    }
  } else if (type.value === 'empty') {
    dataModelsOnWorkflow = [...(workflow.value.dataModels || []), {
      name: entityName || ('New Entity ' + ((workflow.value.dataModels?.length || 0) + 1)),
      type: 'Dimension',
      dataFields: []
    }]
  }

  if (dataModelsOnWorkflow) {
    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: dataModelsOnWorkflow?.length > 0 ? dataModelsOnWorkflow : undefined,
      workflow: workflow.value
    })

    if (suggestedEntitiesExist) {
      store.commit('snackbar/showMessage', { content: 'All suggested entities already exist!' }, { root: true })
    }
  }

  proxy.$loadingState.stop()
  toggleDialog(false)
}
</script>
